/* global BigInt */
/**
 * useElectronicRanges — per-agency electronic-power range cache.
 *
 * Fetches the /electronic-ranges endpoint on first use for a given
 * agencyId and caches the result in a module-level Map so that every
 * badge in the tree (Power Log, Inquiry, Inventory, Discharges, Large
 * Bonds) shares a single network call per agency.
 *
 * Returns:
 *   {
 *     loading,
 *     isElectronic(powerNumber) -> matching range | null,
 *   }
 *
 * Non-numeric power numbers short-circuit to `null` — the ranges are
 * numeric on the backend, so a badge on a non-numeric power would be
 * meaningless.
 */

import { useEffect, useMemo, useState } from 'react';
import { getElectronicRanges } from './oathElectronicPowerService';

// Shared across every consumer in the app for the current session.
// Key = agencyId (coerced to string). Value = { promise, parsedRanges }.
// Holding the promise means concurrent callers on the first paint of a
// page dedupe cleanly instead of firing N identical fetches.
const CACHE = new Map();

function keyFor(agencyId) {
  return agencyId == null ? '' : String(agencyId);
}

// Convert the wire payload once and reuse. Each range's begin/end are
// pre-parsed to BigInt so the per-power check is a couple of comparisons
// rather than string parsing on every render.
function parseRanges(raw) {
  const out = [];
  for (const r of raw || []) {
    const b = r?.beginningPowerRange;
    const e = r?.endingPowerRange;
    if (typeof b !== 'string' || typeof e !== 'string') continue;
    if (!/^\d+$/.test(b) || !/^\d+$/.test(e)) continue;
    try {
      out.push({
        begin: BigInt(b),
        end:   BigInt(e),
        beginStr: b,
        endStr:   e,
        powerAmountCd:          r.powerAmountCd,
        powerAmountDescription: r.powerAmountDescription,
        powerExpirationDate:    r.powerExpirationDate,
        agentId:                r.agentId,
      });
    } catch {
      /* skip un-parseable */
    }
  }
  return out;
}

async function loadInto(cache, agencyId) {
  const key = keyFor(agencyId);
  const promise = getElectronicRanges(agencyId).then((resp) => {
    const raw = resp?.success ? (resp.data || []) : [];
    const parsedRanges = parseRanges(raw);
    cache.set(key, { promise: null, parsedRanges });
    return parsedRanges;
  });
  cache.set(key, { promise, parsedRanges: null });
  return promise;
}

export function useElectronicRanges(agencyId) {
  const [ranges, setRanges] = useState(() => {
    const cached = CACHE.get(keyFor(agencyId));
    return cached?.parsedRanges || null;
  });
  const [loading, setLoading] = useState(() => {
    const cached = CACHE.get(keyFor(agencyId));
    return cached ? cached.parsedRanges == null : agencyId != null;
  });

  useEffect(() => {
    if (agencyId == null) {
      setRanges(null);
      setLoading(false);
      return;
    }
    const key = keyFor(agencyId);
    const cached = CACHE.get(key);

    if (cached?.parsedRanges != null) {
      setRanges(cached.parsedRanges);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const p = cached?.promise || loadInto(CACHE, agencyId);
    p.then((parsedRanges) => {
      if (cancelled) return;
      setRanges(parsedRanges);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [agencyId]);

  const isElectronic = useMemo(() => {
    const list = ranges || [];
    return (powerNumber) => {
      if (powerNumber == null) return null;
      const s = String(powerNumber).trim();
      if (!/^\d+$/.test(s)) return null;
      let bn;
      try { bn = BigInt(s); } catch { return null; }
      for (const r of list) {
        if (bn >= r.begin && bn <= r.end) return r;
      }
      return null;
    };
  }, [ranges]);

  return { loading, isElectronic };
}

// Escape hatch for tests + hard refresh: forget one agency's cache
// (or everything if no id is passed).
export function _resetElectronicRangesCache(agencyId) {
  if (agencyId == null) {
    CACHE.clear();
  } else {
    CACHE.delete(keyFor(agencyId));
  }
}
